/**
 * Primary navigation, built from the page registries.
 *
 * The two menus list what the registries say is live, so a topic appears in
 * the header the moment its page ships and never before — a hand-kept menu is
 * how a site ends up linking to pages that do not exist.
 */
import { GROWTH_SYSTEMS, GROWTH_SYSTEMS_HUB, growthSystemPath } from './growth-systems'
import { SERVICES, SERVICES_HUB, liveTopicsFor, servicePath, topicPath } from './service-topics'
import { SITE } from './site'

export type NavLink = { label: string; href: string; description?: string }

/** A column in a dropdown. `heading` is itself a destination when present. */
export type NavGroup = { heading?: NavLink; links: NavLink[] }

export type NavMenuItem = {
  kind: 'menu'
  label: string
  /** The hub page, linked at the foot of the panel. */
  hub: NavLink
  groups: NavGroup[]
}

export type NavItem =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'booking'; label: string; href: string }
  | NavMenuItem

const GROWTH_SYSTEMS_MENU: NavMenuItem = {
  kind: 'menu',
  label: 'Growth Systems',
  hub: { label: 'All growth systems', href: GROWTH_SYSTEMS_HUB },
  groups: [
    {
      links: GROWTH_SYSTEMS.map((system) => ({
        label: system.name,
        href: growthSystemPath(system.id),
        description: system.headline,
      })),
    },
  ],
}

const SERVICES_MENU: NavMenuItem = {
  kind: 'menu',
  label: 'Services',
  hub: { label: 'All services', href: SERVICES_HUB },
  groups: SERVICES.map((service) => ({
    heading: {
      label: service.name,
      href: servicePath(service.slug),
      description: service.summary,
    },
    links: liveTopicsFor(service.slug).map((topic) => ({
      label: topic.name,
      href: topicPath(topic),
    })),
  })),
}

export const PRIMARY_NAV: NavItem[] = [
  { kind: 'link', label: 'About', href: '/about-us' },
  GROWTH_SYSTEMS_MENU,
  SERVICES_MENU,
  { kind: 'link', label: 'Tools', href: '/tools' },
  { kind: 'link', label: 'Learn', href: '/learn' },
  { kind: 'link', label: 'Work', href: '/work' },
  { kind: 'booking', label: 'Book a Call', href: SITE.bookingUrl },
]

/** Whether `pathname` is the menu's hub or any page it lists. */
export function menuContainsPath(menu: NavMenuItem, pathname: string): boolean {
  const hrefs = [
    menu.hub.href,
    ...menu.groups.flatMap((g) => [
      ...(g.heading ? [g.heading.href] : []),
      ...g.links.map((l) => l.href),
    ]),
  ]
  return hrefs.some((href) => pathname === href || pathname.startsWith(`${href}/`))
}
